import { rendersOnCard } from '../../domain/commands/coachCard';
import { ARTIFACT_PHASE } from '../../types';
import type {
  AdjustmentEntry,
  CoachCardContext,
  CoachCardRow,
  MatchPurpose,
} from '../../types';

/**
 * B-16 — the **card's pure decisions** (design spec §2.5 step 4; §13 rows 29, 30, 32; scenarios
 * 29–32, 34). DOM-free, so the Node suite drives every arm; `CoachCardView` is rendering over
 * these plus B-3's `rendersOnCard`.
 *
 * Three decisions live here because each is a rule a screenshot cannot settle:
 *
 * 1. **Which framing a card carries** — derived from (purpose, context) and from nothing stored,
 *    so a card cannot be mis-named by a field that drifted. §8's tradeoff row: *"framing derived
 *    from (purpose, context) over a stored framing field — we lose nothing, derivation is total"*.
 * 2. **Which entries the card is made of** — `rendersOnCard` imported from the engine handler that
 *    guards `ERR_CARD_EMPTY`, never restated. The page count, the "(n)" on the finalize button and
 *    the guard are then one rule with three readers, and a rejected suggestion cannot reach a page.
 * 3. **Which page a scroll position is on** — the pager is a scroll-snap strip (skill §7: floor-safe,
 *    indicators below the content), so "which page am I on" is arithmetic over `scrollLeft`, and it
 *    has boundary behaviour worth pinning rather than discovering at a pitch.
 */

// ── The three framings (§2.5's table; scenario 32) ───────────────────────────

/** One framing: the name the user reads, the accent key the stylesheet colours, and the "when". */
export interface CardFramingB {
  /**
   * The stylesheet/probe key. Deliberately NOT the context value: two of the three framings share
   * a context (`fullTime`), so keying on context would collapse two names into one accent. (It is
   * also why these read `adjustments`/`review`/`emphasis` rather than the moment they belong to —
   * a match state is never spelled bare outside `domain/`, the `ses-1` rule.)
   */
  readonly key: 'adjustments' | 'review' | 'emphasis';
  /** The card's title — scenario 32's "different name". */
  readonly title: string;
  /** One line naming the moment of use, for a card re-read weeks later on the opponent file. */
  readonly lead: string;
}

const HALFTIME_ADJUSTMENTS: CardFramingB = {
  key: 'adjustments',
  title: 'Halftime adjustments',
  lead: 'What to tell the team at the break.',
};

const FULL_TIME_REVIEW: CardFramingB = {
  key: 'review',
  title: 'Full-time review',
  lead: 'What the match said, for afterwards.',
};

const POINTS_OF_EMPHASIS: CardFramingB = {
  key: 'emphasis',
  title: 'Points of emphasis',
  lead: 'From scouting them — for the day you play them.',
};

/**
 * §2.5's framing table as data. Computed keys through `ARTIFACT_PHASE` (the `ses-1` rule: a match
 * state is never spelled bare outside `domain/`), and a **total** `Record` over both unions — a
 * fourth (purpose, context) pair cannot appear without this object refusing to compile, which is
 * scenario 32's "no fourth exists" enforced at build time rather than asserted in prose.
 *
 * `advanceScout × halftime` is `null` **by design, not by omission**: scenario 20 gives an advance
 * scout no halftime workflow at all, and `handleUpsertCoachCardDraft` rejects the pair outright
 * (`ERR_CARD_CONTEXT`). The row exists so the absence is a stated answer.
 */
const FRAMINGS: Record<MatchPurpose, Record<CoachCardContext, CardFramingB | null>> = {
  teamMatch: {
    [ARTIFACT_PHASE.halftime]: HALFTIME_ADJUSTMENTS,
    [ARTIFACT_PHASE.fullTime]: FULL_TIME_REVIEW,
  },
  advanceScout: {
    [ARTIFACT_PHASE.halftime]: null,
    [ARTIFACT_PHASE.fullTime]: POINTS_OF_EMPHASIS,
  },
};

/**
 * Scenario 32 — the card's framing for this (purpose, context), or `null` where there is none.
 *
 * `null` on a purpose-less match is the same answer for the same reason: the framing is a fact
 * about *why the match was watched*, and a match with no purpose has not said. It is unreachable
 * through B (the setup gate requires a purpose, and §4.4's import repair backfills `'teamMatch'`
 * into every pre-v004 archive) — but the column is nullable, so the branch is answered rather than
 * assumed. The surfaces render such a card **unnamed and complete**: nothing is hidden, and no
 * fourth name is invented to cover a coordinate the design does not have one for.
 */
export function framingForB(
  purpose: MatchPurpose | null,
  context: CoachCardContext,
): CardFramingB | null {
  if (purpose === null) return null;
  return FRAMINGS[purpose][context];
}

// ── The pages (scenario 29 and its negative) ─────────────────────────────────

/**
 * §2.5 step 4 — the entries the card is made of, **in the order the user arranged them** (the
 * draft's own order: suggestions as they arrived, manual entries appended at the end). There is no
 * ranking here and none anywhere: adjustment reordering is a §14 non-goal, so a second ordering
 * rule would be a fourth thing to disagree with.
 *
 * The filter is `rendersOnCard` itself — the engine's predicate, imported. Scenario 29's negative
 * ("rejected/suggested entries absent") is therefore not a second implementation of the rule that
 * could drift out of step with `ERR_CARD_EMPTY`; it is the same function answering the same
 * question for a different reader.
 */
export function cardPagesB(card: CoachCardRow | null): readonly AdjustmentEntry[] {
  if (card === null) return [];
  return card.adjustments.filter(rendersOnCard);
}

// ── The pager (skill §7) ─────────────────────────────────────────────────────

/**
 * Which page a scroll offset is showing.
 *
 * `round`, not `floor`: a snapped strip settles a fraction of a pixel short of its boundary often
 * enough that flooring reports the previous page while the next one fills the screen — the dots
 * would then disagree with what the user is reading. Clamped to the real page range so a rubber-band
 * overscroll (or a stale measurement mid-resize) can never index past the end, and defensive about a
 * zero/non-finite width because a pager measured before layout reports exactly that.
 */
export function pageFromScrollB(scrollLeft: number, pageWidth: number, pageCount: number): number {
  if (pageCount <= 0) return 0;
  if (!Number.isFinite(scrollLeft) || !Number.isFinite(pageWidth) || pageWidth <= 0) return 0;
  const index = Math.round(scrollLeft / pageWidth);
  if (!Number.isFinite(index)) return 0;
  return Math.min(Math.max(index, 0), pageCount - 1);
}

// ── The full-time flow's reference card (§2.5 step 4's last sentence) ────────

/**
 * §2.5 — *"The halftime card (finalized or still draft) is shown as reference context at the top of
 * the full-time flow."*
 *
 * Draft **or** final, deliberately: at full time the halftime draft may still be unfinished (its
 * window includes `fullTime` precisely so a whistled-away draft can be finished after the match),
 * and the half's thinking is context for the full-time card either way.
 *
 * Never at the halftime flow itself — there the halftime card *is* the card being built, and
 * showing it above itself would put the same entries on screen twice with two different meanings.
 */
export function referenceCardB(
  context: CoachCardContext,
  halftimeCard: CoachCardRow | null | undefined,
): CoachCardRow | null {
  if (context !== ARTIFACT_PHASE.fullTime) return null;
  return halftimeCard ?? null;
}
